import { useCallback, useEffect, useState } from "react";
import Badge from "@/components/base/Badge";
import { ecosystemUnits } from "@/mocks/site";
import { buildWhatsAppLink } from "@/utils/links";

type SlideBadgeTone = "gold" | "rose" | "clinic";

type Slide = {
  id: string;
  badge: string;
  badgeTone: SlideBadgeTone;
  eyebrow: string;
  title: string;
  phrase: string;
  ctaLabel: string;
  ctaHref: string;
  ctaIcon: string;
  image: string;
  alt: string;
};

const slides: Slide[] = [
  {
    id: "tratamientos",
    badge: "Más solicitado",
    badgeTone: "gold",
    eyebrow: "Clínica estética · Culiacán",
    title: "Belleza clínica, natural",
    phrase: "Tratamientos con valoración previa y resultados que se ven como tú.",
    ctaLabel: "Agendar valoración",
    ctaHref: buildWhatsAppLink("Hola ALÉA, quiero agendar una valoración."),
    ctaIcon: "ri-calendar-check-line",
    image:
      "https://readdy.ai/api/search-image?query=Bright%20clean%20editorial%20beauty%20photograph%20of%20a%20woman%20with%20luminous%20natural%20skin%20receiving%20a%20skin%20care%20treatment%20on%20a%20soft%20warm%20white%20clinic%20background%2C%20modern%20clinical%20aesthetic%20campaign%20photography%2C%20fresh%20natural%20daylight%2C%20elegant%20and%20minimal%2C%20generous%20negative%20space%20on%20one%20side%2C%20no%20text&width=1600&height=1000&seq=aleav-hero-s1&orientation=landscape",
    alt: "Mujer con piel natural y luminosa durante un tratamiento en ALÉA Aesthetic House",
  },
  {
    id: "promo",
    badge: "Oferta activa",
    badgeTone: "rose",
    eyebrow: "Promoción de temporada",
    title: "Ritual Glow",
    phrase: "Limpieza profunda y aparatología para piel luminosa, con precio especial por tiempo limitado.",
    ctaLabel: "Ver promociones",
    ctaHref: "#promos",
    ctaIcon: "ri-price-tag-3-line",
    image:
      "https://readdy.ai/api/search-image?query=Bright%20clean%20beauty%20campaign%20photo%20of%20a%20glowing%20facial%20skin%20care%20ritual%20with%20soft%20warm%20golden%20light%20on%20a%20white%20studio%20background%2C%20modern%20premium%20clinical%20aesthetic%20editorial%2C%20fresh%20luminous%20skin%2C%20elegant%20minimal%20composition%20with%20negative%20space%2C%20no%20text&width=1600&height=1000&seq=aleav-hero-s2&orientation=landscape",
    alt: "Ritual facial glow con luz dorada suave de la promoción ALÉA",
  },
  {
    id: "skin",
    badge: "ALÉA Skin",
    badgeTone: "clinic",
    eyebrow: "Skincare profesional",
    title: "Tu rutina en casa",
    phrase: "Hidrata, protege y mantén tus resultados con nuestra línea profesional de cuidado.",
    ctaLabel: "Comprar productos",
    ctaHref: "#productos",
    ctaIcon: "ri-shopping-bag-3-line",
    image:
      "https://readdy.ai/api/search-image?query=Bright%20clean%20flat%20lay%20of%20premium%20skincare%20products%20with%20elegant%20white%20packaging%20arranged%20on%20a%20soft%20warm%20white%20seamless%20surface%20with%20gentle%20shadows%2C%20modern%20clinical%20ecommerce%20campaign%20photography%2C%20minimal%20and%20bright%20with%20negative%20space%2C%20no%20text&width=1600&height=1000&seq=aleav-hero-s3&orientation=landscape",
    alt: "Productos de skincare profesional ALÉA Skin sobre fondo claro",
  },
];

const quickActions = [
  {
    label: "Agendar valoración",
    href: buildWhatsAppLink("Hola ALÉA, quiero agendar una valoración."),
    variant: "primary",
    icon: "ri-calendar-check-line",
  },
  { label: "Ver promociones", href: "#promos", variant: "outline", icon: "ri-price-tag-3-line" },
  { label: "Comprar productos", href: "#productos", variant: "promo", icon: "ri-shopping-bag-3-line" },
];

const trustItems = [
  { icon: "ri-shield-check-line", text: "Protocolos con valoración previa" },
  { icon: "ri-leaf-line", text: "Resultados naturales" },
  { icon: "ri-store-2-line", text: "5 unidades en Culiacán" },
];

const variantClasses: Record<string, string> = {
  primary: "cta-primary bg-primary-500 text-background-50 hover:bg-primary-600",
  outline:
    "cta-outline border border-secondary-300 bg-background-50 text-foreground-950 hover:border-accent-400",
  promo: "btn-promo",
};

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % slides.length), 5600);
    return () => clearInterval(timer);
  }, [paused]);

  const active = slides[index];

  return (
    <section
      id="inicio"
      className="relative w-full overflow-hidden bg-background-50 pt-[68px] md:pt-[80px]"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[460px] glow-top" />
      <div className="pointer-events-none absolute -left-24 top-40 hidden h-[420px] w-[420px] rounded-full glow-rose blur-3xl lg:block" />
      <div className="pointer-events-none absolute right-0 top-24 hidden h-[460px] w-[520px] rounded-full glow-lilac blur-3xl lg:block" />

      <div className="relative mx-auto w-full max-w-[1400px] px-5 pb-12 pt-10 md:px-10 md:pb-20 md:pt-14">
        <div
          className="relative mt-0"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="pointer-events-none absolute -left-10 -top-8 h-56 w-56 rounded-full glow-gold blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 right-8 h-64 w-64 rounded-full glow-rose blur-3xl" />

          <div className="card-soft-lg relative h-[460px] w-full overflow-hidden rounded-[28px] bg-background-100 md:h-[560px] lg:h-[600px]">
            {slides.map((slide, i) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
                  i === index ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
                aria-hidden={i !== index}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  title={`${slide.title} — ALÉA Aesthetic House Culiacán`}
                  className={`h-full w-full object-cover object-center transition-transform duration-[6000ms] ease-out ${
                    i === index ? "scale-105" : "scale-100"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/50 to-primary-950/10" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-primary-950/75 via-primary-950/25 to-transparent" />
              </div>
            ))}

            <span className="pointer-events-none absolute left-5 top-5 h-16 w-16 rounded-tl-[22px] border-l border-t border-accent-400/70" />
            <span className="pointer-events-none absolute bottom-5 right-5 hidden h-16 w-16 rounded-br-[22px] border-b border-r border-accent-400/70 md:block" />

            <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 lg:p-14">
              <div key={active.id} className="max-w-xl animate-fade-up">
                <Badge tone={active.badgeTone} icon="ri-flashlight-fill">
                  {active.badge}
                </Badge>
                <span className="mt-4 flex items-center gap-3 font-label text-[10px] uppercase tracking-[0.22em] text-accent-300">
                  <span className="h-px w-7 bg-accent-300/70" />
                  {active.eyebrow}
                </span>
                <h1 className="mt-3 font-heading text-[38px] font-extrabold leading-[1.02] tracking-[-0.02em] text-background-50 sm:text-5xl md:text-[60px]">
                  {active.title}
                </h1>
                <p className="mt-4 max-w-md text-base leading-snug text-background-200 md:text-lg">
                  {active.phrase}
                </p>

                <a
                  href={active.ctaHref}
                  target={active.ctaHref.startsWith("http") ? "_blank" : undefined}
                  rel={active.ctaHref.startsWith("http") ? "noreferrer" : undefined}
                  className="cta-gold mt-7 inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-[16px] px-6 font-label text-[11px] font-semibold uppercase tracking-[0.14em]"
                >
                  <i className={`${active.ctaIcon} text-base`} />
                  {active.ctaLabel}
                </a>
              </div>
            </div>

            <div className="absolute right-5 top-5 flex items-center gap-2 md:right-8 md:top-8">
              {slides.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Ir a la campaña ${i + 1}: ${slide.title}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? "w-8 bg-accent-400" : "w-3 bg-background-50/50 hover:bg-background-50/80"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="Campaña anterior"
              className="absolute left-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/30 text-background-50 backdrop-blur-sm transition-colors duration-300 hover:bg-primary-950/60 md:flex"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="Siguiente campaña"
              className="absolute right-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/30 text-background-50 backdrop-blur-sm transition-colors duration-300 hover:bg-primary-950/60 md:flex"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          </div>
        </div>

        <div className="mt-8 grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          {quickActions.map((action) => (
            <a
              key={action.label}
              href={action.href}
              target={action.href.startsWith("http") ? "_blank" : undefined}
              rel={action.href.startsWith("http") ? "noreferrer" : undefined}
              className={`inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-[16px] px-6 font-label text-[11px] font-semibold uppercase tracking-[0.12em] transition-all duration-300 ${variantClasses[action.variant]}`}
            >
              <i className={`${action.icon} text-base`} />
              {action.label}
            </a>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-6 border-t border-secondary-200 pt-7 lg:flex-row lg:items-center lg:justify-between">
          <ul className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-6">
            {trustItems.map((item) => (
              <li key={item.text} className="flex items-center gap-2.5 text-sm text-foreground-600">
                <i className={`${item.icon} text-lg text-accent-600`} />
                {item.text}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <span className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
              Un ecosistema completo
            </span>
            {ecosystemUnits.map((unit) => (
              <a
                key={unit.name}
                href={unit.href}
                className="group inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-foreground-950"
              >
                <span className="font-heading text-xs font-bold text-accent-700">{unit.index}</span>
                <span className="font-medium">{unit.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}